import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  PhotoCamera as PhotoIcon,
  CloudUpload as UploadIcon,
  Notes as NotesIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Flag as FlagIcon,
  Cancel as CancelIcon // Added CancelIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { workOrdersApi } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import WorkOrderTimeProgressBar from './components/WorkOrderTimeProgressBar';
import AuthenticatedImage from '../common/AuthenticatedImage';
import WorkOrderDetailsModal from './components/WorkOrderDetailsModal';
import DefectContextViewer from './components/DefectContextViewer';
import DisciplineAccessControl from '../common/DisciplineAccessControl';
const statusColors = {
  pending: 'default',
  assigned: 'info',
  in_progress: 'warning',
  completed_temporary: 'success',
  completed: 'success',
  rejected: 'error',
  cancelled: 'default'
};

const priorityColors = {
  low: 'default',
  normal: 'info',
  high: 'warning',
  critical: 'error'
};

// Timeline Status Component with Clickable Milestones
const StatusTimeline = ({ currentStatus, workOrder, onMilestoneClick }) => {
  const statuses = [
    { key: 'assigned', label: 'Assigned', color: '#2196f3' },
    { key: 'in_progress', label: 'In Progress', color: '#ff9800' },
    { key: 'completed_temporary', label: 'Temp. Completed', color: '#9ccc65' },
    { key: 'completed', label: 'Completed', color: '#4caf50' }
  ];

  if (currentStatus === 'cancelled') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CancelIcon color="error" />
        <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
          Cancelled
        </Typography>
      </Box>
    );
  }

  const getCurrentStatusIndex = () => {
    // Handle different possible status values that might come from the API
    const statusMap = {
      'assigned': 0,
      'in_progress': 1,
      'in progress': 1, // Handle space variant
      'completed_temporary': 2,
      'completed': 3,
      'pending': 0, // Map pending to assigned
      'started': 1, // Map started to in_progress
      'finished': 3, // Map finished to completed
      'done': 3 // Map done to completed
    };
    
    const normalizedStatus = currentStatus?.toLowerCase() || 'assigned';
    return statusMap[normalizedStatus] !== undefined ? statusMap[normalizedStatus] : 0;
  };

  const currentIndex = getCurrentStatusIndex();

  const handleMilestoneClick = (status, index) => {
    // Allow clicking on completed milestones and current milestone for viewing, any future milestone for editing
    if (index <= currentIndex) {
      // Completed or current milestone - show view modal
      onMilestoneClick(workOrder, status, index, 'view');
    } else {
      // Any future milestone - allow editing
      onMilestoneClick(workOrder, status, index, 'edit');
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      minWidth: 240,
      py: 1
    }}>
      {statuses.map((status, index) => (
        <Box key={status.key} sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Status Circle */}
          <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              onClick={() => handleMilestoneClick(status.key, index)}
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                backgroundColor: index <= currentIndex ? status.color : '#e0e0e0',
                border: index === currentIndex ? `3px solid ${status.color}` : '2px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: index === currentIndex ? `0 0 0 4px ${status.color}20` : 'none',
                transition: 'all 0.3s ease',
                position: 'relative',
                zIndex: 1,
                cursor: 'pointer',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: `0 0 0 6px ${status.color}30`
                }
              }}
            >
              {index < currentIndex && (
                <CheckCircleOutlineIcon
                  sx={{
                    fontSize: 14,
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              )}
              {index === currentIndex && (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    animation: index === currentIndex ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                      '50%': {
                        transform: 'scale(1.1)',
                        opacity: 0.7,
                      },
                      '100%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                    }
                  }}
                />
              )}
            </Box>
            {/* Status Label */}
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                fontSize: '0.65rem',
                fontWeight: index === currentIndex ? 700 : 500,
                color: index <= currentIndex ? status.color : '#999',
                textAlign: 'center',
                minWidth: 55,
                lineHeight: 1.2
              }}
            >
              {status.label}
            </Typography>
          </Box>
          
          {/* Connecting Line */}
          {index < statuses.length - 1 && (
            <Box
              sx={{
                width: 35,
                height: 3,
                backgroundColor: index < currentIndex ? statuses[index + 1].color : '#e0e0e0',
                mx: 1.5,
                borderRadius: 1,
                transition: 'backgroundColor 0.3s ease',
                position: 'relative',
                '&::after': index === currentIndex - 1 ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: statuses[index + 1].color,
                  borderRadius: 1,
                  animation: 'progressFill 0.5s ease-out'
                } : {},
                '@keyframes progressFill': {
                  '0%': { width: '0%' },
                  '100%': { width: '100%' }
                }
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

const ContractorWorkOrderDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  
  // Status Update Modal states
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [updating, setUpdating] = useState(false);
  
  // View Modal states (for completed milestones)
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewMilestoneData, setViewMilestoneData] = useState(null);
  const [loadingMilestoneData, setLoadingMilestoneData] = useState(false);

  // Work order details modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedWorkOrderDetails, setSelectedWorkOrderDetails] = useState(null);
  // const [workOrderActivities, setWorkOrderActivities] = useState([]); // Moved to modal
  // const [workOrderPhotos, setWorkOrderPhotos] = useState([]); // Moved to modal
  // const [loadingWorkOrderDetails, setLoadingWorkOrderDetails] = useState(false); // Moved to modal

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await workOrdersApi.getMyAssignments();

      // The API returns an object { message, data, count }
      // We need to extract the 'data' property which is the array of work orders
      if (response.data && Array.isArray(response.data.data)) {
        setWorkOrders(response.data.data);
      } else {
        // Handle cases where the data format is not what we expect
        console.warn("Work orders data is not in the expected format:", response.data);
        setWorkOrders([]);
      }
    } catch (error) {
      console.error('Failed to fetch work orders:', error);
      setError('Failed to load work orders. Please try again.');
      setWorkOrders([]); // Clear work orders on error
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchWorkOrders();
    setRefreshing(false);
  };

  const handleViewDetails = (workOrder) => {
    setSelectedWorkOrderDetails(workOrder);
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedWorkOrderDetails(null);
  };

  // Handle milestone click
  const handleMilestoneClick = async (workOrder, status, index, mode) => {
    setSelectedWorkOrder(workOrder);
    setSelectedStatus(status);
    
    if (mode === 'view') {
      // Fetch historical data for completed milestone
      await fetchMilestoneData(workOrder.id, status);
      setViewModalOpen(true);
    } else {
      // Edit mode for new milestone
    setStatusNotes('');
      setSelectedPhoto(null);
    setStatusModalOpen(true);
    }
  };

  // Fetch milestone data for viewing
  const fetchMilestoneData = async (workOrderId, status) => {
    try {
      setLoadingMilestoneData(true);
      
      // Fetch activities and photos for this milestone
      const [activitiesResponse, photosResponse] = await Promise.all([
        workOrdersApi.getActivities(workOrderId),
        workOrdersApi.getPhotos(workOrderId)
      ]);

      // Filter activities for this specific status update
      const statusActivities = activitiesResponse.data.data.filter(
        activity => activity.activity_type === 'status_update' && 
                   activity.new_status === status
      ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      // Filter photos from around the same time as the status update
      const milestoneActivity = statusActivities[0]; // Get the most recent status update
      let milestonePhotos = [];
      
      if (milestoneActivity) {
        const activityDate = new Date(milestoneActivity.created_at);
        const timeWindow = 30 * 60 * 1000; // 30 minutes window
        
        milestonePhotos = photosResponse.data.data.filter(photo => {
          const photoDate = new Date(photo.created_at);
          return Math.abs(photoDate - activityDate) <= timeWindow;
        });
      }

      setViewMilestoneData({
        status,
        activity: milestoneActivity,
        photos: milestonePhotos,
        updatedAt: milestoneActivity?.created_at
      });
    } catch (error) {
      console.error('Failed to fetch milestone data:', error);
      setViewMilestoneData(null);
    } finally {
      setLoadingMilestoneData(false);
    }
  };

  // Handle photo selection
  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedPhoto(file);
    }
  };

  // Format date and time for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // Submit status update
  const handleStatusSubmit = async () => {
    if (!selectedWorkOrder || !selectedStatus) return;

    try {
      setUpdating(true);
      setError('');

      // 1. Update work order status based on selected status
      if (selectedStatus === 'completed') {
        const completionData = new FormData();
        completionData.append('completion_notes', statusNotes || 'Work order completed.');
        if (selectedPhoto) {
          completionData.append('photos', selectedPhoto);
        }
        await workOrdersApi.completeWorkOrder(selectedWorkOrder.id, completionData);
      } else {
        // For in_progress and completed_temporary, we need to handle photo uploads
        if (selectedPhoto) {
          const formData = new FormData();
          formData.append('photos', selectedPhoto);
          formData.append('photo_type', 'progress');
          formData.append('caption', statusNotes || `Status update: ${selectedStatus}`);
          
          await workOrdersApi.uploadPhotos(selectedWorkOrder.id, formData);
        }

        await workOrdersApi.updateStatus(selectedWorkOrder.id, {
          status: selectedStatus,
          notes: statusNotes || `Status updated to ${selectedStatus}`
        });
      }

      setStatusModalOpen(false);
      setSelectedWorkOrder(null);
      setSelectedStatus('');
      setStatusNotes('');
      setSelectedPhoto(null);
      await fetchWorkOrders(); // Refresh the list
    } catch (error) {
      console.error('Failed to update work order status:', error);
      setError('Failed to update work order status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  // Calculate stats for all work orders
  const getStatsCards = () => {
    const assigned = workOrders.filter(wo => wo.status === 'assigned');
    const inProgress = workOrders.filter(wo => wo.status === 'in_progress');
    const completed = workOrders.filter(wo => wo.status === 'completed');
    const cancelled = workOrders.filter(wo => wo.status === 'cancelled');
    
    return [
      {
        title: 'Assigned',
        count: assigned.length,
        color: 'info',
        description: 'Ready to start'
      },
      {
        title: 'In Progress',
        count: inProgress.length,
        color: 'warning',
        description: 'Currently working'
      },
      {
        title: 'Completed',
        count: completed.length,
        color: 'success',
        description: 'Finished work'
      },
      {
        title: 'Cancelled',
        count: cancelled.length,
        color: 'error',
        description: 'Cancelled assignments'
      }
    ];
  };

  if (loading) {
    return <LoadingSpinner message="Loading your work orders..." />;
  }

  return (
    <DisciplineAccessControl requiredDiscipline="Road Pavement">
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          My Action Log
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {getStatsCards().map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {stat.count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.description}
                    </Typography>
                  </Box>
                  <Chip 
                    label={stat.count} 
                    color={stat.color} 
                    sx={{ fontSize: '1.2rem', height: 40, minWidth: 40 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Work Orders Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Work Order</TableCell>
                <TableCell>Asset/Location</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Time Progress</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No work orders found for this status
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                workOrders.map((workOrder) => (
                  <TableRow key={workOrder.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {workOrder.work_order_number}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {workOrder.title}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title={`Lat: ${workOrder.latitude || 'N/A'}, Lon: ${workOrder.longitude || 'N/A'}`}>
                          <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        </Tooltip>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {workOrder.location_description || `Lat: ${workOrder.latitude?.toFixed(6) || 'N/A'}, Lon: ${workOrder.longitude?.toFixed(6) || 'N/A'}`}
                          </Typography>
                          {workOrder.asset_name && (
                            <Typography variant="body2" color="textSecondary">
                              Asset: {workOrder.asset_name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={workOrder.priority}
                        color={priorityColors[workOrder.priority]}
                        size="small"
                        variant={workOrder.priority === 'critical' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      {workOrder.due_date ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ScheduleIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {new Date(workOrder.due_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No due date
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: '280px', minWidth: '280px' }}>
                      <WorkOrderTimeProgressBar
                        createdAt={workOrder.created_at}
                        dueDate={workOrder.due_date}
                        status={workOrder.status}
                        priority={workOrder.priority}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusTimeline 
                        currentStatus={workOrder.status} 
                        workOrder={workOrder}
                        onMilestoneClick={handleMilestoneClick}
                      />
                    </TableCell>
                    <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(workOrder)}
                          color="primary"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Status Update Modal */}
      <Dialog 
        open={statusModalOpen} 
        onClose={() => setStatusModalOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: theme.shadows[16],
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        {/* COMPACT HEADER */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          py: 1.5,
          px: 2.5,
          flexShrink: 0,
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon color="primary" fontSize="small" /> 
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Update Work Progress
            </Typography>
            {selectedWorkOrder && (
              <Chip 
                label={`${selectedWorkOrder.work_order_number}`}
                size="small" 
                variant="outlined"
                sx={{ ml: 1, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          <IconButton onClick={() => setStatusModalOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        
        {/* DYNAMIC CONTENT AREA */}
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex',
          overflow: 'hidden',
          minHeight: '400px',
          maxHeight: 'calc(90vh - 120px)',
        }}>
          <Box sx={{ display: 'flex', width: '100%' }}>
            
            {/* LEFT SIDE - DEFECT CONTEXT (PRIORITY) */}
            <Box sx={{ 
              width: '60%',
              display: 'flex',
              flexDirection: 'column',
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`
            }}>
              <DefectContextViewer workOrder={selectedWorkOrder} />
            </Box>
            
            {/* RIGHT SIDE - WORK ORDER & UPDATE FORM */}
            <Box sx={{ 
              width: '40%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              {/* Work Order Summary Header */}
              <Box sx={{ 
                px: 2, 
                py: 1.5, 
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                bgcolor: alpha(theme.palette.grey[50], 0.3)
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.9rem' }}>
                  <ViewIcon fontSize="small" />
                  Work Order Summary
                </Typography>
              </Box>

              {/* Compact Work Order Information */}
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 1, fontSize: '0.9rem', lineHeight: 1.3 }}>
                  {selectedWorkOrder?.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem', lineHeight: 1.3 }}>
                  {selectedWorkOrder?.location_description || 'No location specified'}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={selectedWorkOrder?.priority}
                    color={priorityColors[selectedWorkOrder?.priority]}
                    size="small"
                    variant={selectedWorkOrder?.priority === 'critical' ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.7rem' }}
                  />
                  <Chip
                    label={selectedWorkOrder?.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    color={statusColors[selectedWorkOrder?.status]}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                  />
                </Box>
              </Box>
              {/* Form Header */}
              <Box sx={{ 
                px: 2, 
                py: 1.5, 
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                bgcolor: alpha(theme.palette.grey[50], 0.3)
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.9rem' }}>
                  <CheckCircleIcon fontSize="small" />
                  Progress Update
                </Typography>
              </Box>

              {/* Form Content */}
              <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                
                {/* Status Selection */}
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary', fontSize: '0.85rem' }}>
                  New Status
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select Status</InputLabel>
            <Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    label="Select Status"
                    size="small"
                  >
                    <MenuItem value="in_progress">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="In Progress" color="warning" size="small" />
                        <Typography variant="body2">Mark as In Progress</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="completed_temporary">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Completed (Temporary)" color="success" variant="outlined" size="small" />
                        <Typography variant="body2">Mark as Completed (Temporary)</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="completed">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Completed" color="success" size="small" />
                        <Typography variant="body2">Mark as Completed</Typography>
                      </Box>
                    </MenuItem>
            </Select>
          </FormControl>

                {/* Progress Notes */}
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary', fontSize: '0.85rem' }}>
                  Progress Notes
                </Typography>

          <TextField
                  label="Describe the work progress..."
            value={statusNotes}
            onChange={(e) => setStatusNotes(e.target.value)}
            fullWidth
            multiline
                  rows={3}
                  placeholder="Add details about the work completed, any issues encountered, or next steps..."
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />

                {/* Photo Upload */}
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary', fontSize: '0.85rem' }}>
                  Progress Photo (Optional)
                </Typography>
                
                <Box sx={{ 
                  border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: 1.5,
                  p: 2,
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  mb: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    bgcolor: alpha(theme.palette.primary.main, 0.05)
                  }
                }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<PhotoIcon />}
                    size="small"
                    sx={{ 
                      borderStyle: 'solid',
                      borderWidth: '1px',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem'
                    }}
                  >
                    {selectedPhoto ? 'Change Photo' : 'Choose Photo'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handlePhotoChange}
                    />
                  </Button>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                    Upload work progress photo (Max 10MB)
                  </Typography>
                </Box>

                {selectedPhoto && (
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    mb: 2
                  }}>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, mb: 0.5 }}>
                      📎 Photo Selected
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedPhoto.name} ({(selectedPhoto.size / 1024 / 1024).toFixed(2)} MB)
                    </Typography>
                  </Box>
                )}

                {/* User Info */}
                <Box sx={{ 
                  mt: 'auto',
                  pt: 2,
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: 'text.secondary'
                }}>
                  <Box sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '50%', 
                    bgcolor: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8rem',
                    color: 'white'
                  }}>
                    👤
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    Updating as {user?.fullName || 'Unknown User'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        
        {/* COMPACT FOOTER */}
        <DialogActions sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          justifyContent: 'space-between',
          flexShrink: 0,
          bgcolor: 'background.paper'
        }}>
          <Button 
            onClick={() => setStatusModalOpen(false)} 
            disabled={updating}
            color="inherit"
            size="medium"
          >
            Cancel
          </Button>
          
          <Button 
            onClick={handleStatusSubmit} 
            variant="contained" 
            disabled={updating || !selectedStatus}
            startIcon={updating ? <CircularProgress size={16} /> : <UploadIcon />}
            size="medium"
            sx={{ minWidth: 140 }}
          >
            {updating ? 'Updating...' : 'Submit Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Milestone Modal */}
      <Dialog 
        open={viewModalOpen} 
        onClose={() => setViewModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: theme.shadows[16],
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        {/* COMPACT HEADER */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          py: 1.5,
          px: 2.5,
          flexShrink: 0,
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon color="primary" fontSize="small" /> 
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              {viewMilestoneData?.status === selectedWorkOrder?.status ? 'Current Milestone Status' : 'Milestone History'}
          </Typography>
            {selectedWorkOrder && (
              <Chip 
                label={`${selectedWorkOrder.work_order_number}`}
                size="small" 
                variant="outlined"
                sx={{ ml: 1, fontSize: '0.7rem' }}
              />
            )}
            <Chip 
              label={selectedStatus?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              size="small" 
              color={selectedStatus === 'completed' ? 'success' : selectedStatus === 'in_progress' ? 'warning' : 'info'}
              sx={{ ml: 0.5, fontSize: '0.7rem', fontWeight: 600 }}
            />
          </Box>
          <IconButton onClick={() => setViewModalOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        
        {/* DYNAMIC CONTENT AREA */}
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex',
          overflow: 'hidden',
          minHeight: '400px',
          maxHeight: 'calc(90vh - 120px)',
        }}>
          {loadingMilestoneData ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center',
              width: '100%',
              py: 6
            }}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Loading milestone data...
              </Typography>
            </Box>
          ) : viewMilestoneData ? (
            <Box sx={{ display: 'flex', width: '100%' }}>
              
              {/* LEFT SIDE - DEFECT CONTEXT (65%) */}
              <Box sx={{ 
                width: '65%',
                display: 'flex',
                flexDirection: 'column',
                borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`
              }}>
                <DefectContextViewer workOrder={selectedWorkOrder} />
              </Box>
              
              {/* RIGHT SIDE - MILESTONE DETAILS (35%) */}
              <Box sx={{ 
                width: '35%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto'
              }}>
                <Box sx={{ p: 2.5, flex: 1, overflow: 'auto' }}>
                  
                  {/* Work Order Details */}
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                    Work Order Details
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                      {selectedWorkOrder?.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {selectedWorkOrder?.location_description || 'No location specified'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      <Chip
                        label={selectedWorkOrder?.priority}
                        color={priorityColors[selectedWorkOrder?.priority]}
                        size="small"
                        variant={selectedWorkOrder?.priority === 'critical' ? 'filled' : 'outlined'}
                      />
                      <Chip
                        label={selectedWorkOrder?.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        color={statusColors[selectedWorkOrder?.status]}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>

                    {selectedWorkOrder?.due_date && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Due Date</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {new Date(selectedWorkOrder.due_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Milestone Status Update */}
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                    Status Update Details
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Updated On</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {viewMilestoneData.updated_at ? formatDateTime(viewMilestoneData.updated_at) : 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Updated By</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {viewMilestoneData.updated_by_name || 'System'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Status Changed To</Typography>
                      <Chip
                        label={viewMilestoneData.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        color={statusColors[viewMilestoneData.status]}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Box>

                  {/* Progress Notes */}
                  {viewMilestoneData.notes && (
                    <>
                      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                        Progress Notes
                      </Typography>
                      <Box sx={{ 
                        mb: 3,
                        p: 2, 
                        bgcolor: alpha(theme.palette.grey[100], 0.5), 
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`
                      }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                          {viewMilestoneData.notes}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {/* Progress Photos */}
                  {viewMilestoneData.photos && viewMilestoneData.photos.length > 0 && (
                    <>
                      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                        Progress Photos ({viewMilestoneData.photos.length})
                      </Typography>
                      
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
                        gap: 1.5, 
                        mb: 2
                      }}>
                        {viewMilestoneData.photos.map((photo, index) => (
                          <Box 
                            key={index} 
                            sx={{ 
                              position: 'relative', 
                              borderRadius: 2, 
                              overflow: 'hidden',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                              }
                            }}
                          >
                            <AuthenticatedImage
                              src={photo.file_path}
                              alt={photo.caption || `Photo ${index + 1}`}
                              sx={{
                                width: '100%',
                                height: '100px',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              onError={(e) => {
                                const fallback = e.target.nextSibling;
                                if (fallback) {
                                  fallback.style.display = 'flex';
                                }
                                e.target.style.display = 'none';
                              }}
                            />
                            <Box sx={{ 
                              display: 'none', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              height: '100px', 
                              bgcolor: alpha(theme.palette.grey[200], 0.5),
                              color: 'text.secondary',
                              flexDirection: 'column'
                            }}>
                              <PhotoIcon sx={{ fontSize: 20, opacity: 0.5, mb: 0.5 }} />
                              <Typography variant="caption">Image not available</Typography>
                            </Box>
                            
                            {/* Photo caption overlay */}
                            {photo.caption && (
                              <Box sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                bgcolor: 'rgba(0,0,0,0.8)',
                                color: 'white',
                                px: 1,
                                py: 0.5,
                                backdropFilter: 'blur(4px)'
                              }}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontSize: '0.7rem',
                                    lineHeight: 1.2,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {photo.caption}
                                </Typography>
                              </Box>
                            )}

                            {/* Photo index badge */}
                            <Box sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 0.5,
                              fontSize: '0.6rem',
                              fontWeight: 600
                            }}>
                              {index + 1}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary" sx={{ 
                        display: 'block', 
                        textAlign: 'center',
                        fontSize: '0.75rem'
                      }}>
                        Click any photo to view full size
                      </Typography>
                    </>
                  )}

                  {/* No additional info message */}
                  {!viewMilestoneData.notes && (!viewMilestoneData.photos || viewMilestoneData.photos.length === 0) && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      py: 4,
                      color: 'text.secondary',
                      textAlign: 'center'
                    }}>
                      <AccessTimeIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        No additional information available for this milestone.
                      </Typography>
                      <Typography variant="caption">
                        Only the status change was recorded.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center',
              width: '100%',
              py: 6,
              color: 'text.secondary'
            }}>
              <Box sx={{ 
                fontSize: 48, 
                opacity: 0.3, 
                mb: 2 
              }}>
                ⚠️
              </Box>
              <Typography variant="body1">
                Failed to load milestone data.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
                Please try again or contact support if the issue persists.
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        {/* COMPACT FOOTER */}
        <DialogActions sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          justifyContent: 'flex-end',
          flexShrink: 0,
          bgcolor: 'background.paper'
        }}>
          <Button 
            onClick={() => setViewModalOpen(false)}
            color="primary"
            variant="contained" 
            size="medium"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reusable Work Order Details Modal */}
      {selectedWorkOrderDetails && (
        <WorkOrderDetailsModal
          open={detailsModalOpen}
          onClose={handleCloseDetailsModal}
          workOrder={selectedWorkOrderDetails}
        />
      )}
    </Box>
    </DisciplineAccessControl>
  );
};

export default ContractorWorkOrderDashboard; 